/**
 * FranchiseScorecardPage — `/reports/franchise-scorecard`.
 *
 * Wired to GET /v1/reports/franchise-scorecard?franchiseId=&period= on
 * service-reporting-api. Derives KPI scores from mail item delivery data.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Award } from 'lucide-react';

import { getFranchiseScorecardReport } from '../../lib/api/pre-built-reports';
import { ApiError } from '../../lib/api/client';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

const TIER_COLOUR: Record<string, string> = {
  PLATINUM: 'text-purple-700 bg-purple-50',
  GOLD:     'text-amber-700  bg-amber-50',
  SILVER:   'text-gray-700   bg-gray-100',
  BRONZE:   'text-orange-700 bg-orange-50',
};

export function FranchiseScorecardPage(): JSX.Element {
  const report = findPreBuiltReport('franchise-scorecard');
  const [franchiseId, setFranchiseId] = useState<string>('');
  const [period, setPeriod] = useState<string>('THIS_MONTH');

  const query = useQuery({
    queryKey: ['pre-built', 'franchise-scorecard', franchiseId, period],
    queryFn: () => getFranchiseScorecardReport({ franchiseId: franchiseId || undefined, period }),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const data = query.data;
  const denied =
    query.isError && query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403);

  const incentiveEligible = data ? data.rows.filter((r) => r.incentiveEligible).length : null;
  const atRisk = data ? data.rows.filter((r) => r.kpiScore < 70).length : null;

  return (
    <ReportShell report={report}>
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Franchise ID</label>
            <input type="text" value={franchiseId} onChange={(e) => setFranchiseId(e.target.value)}
              placeholder="Leave blank for all" aria-label="Franchise ID"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Period</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white">
              <option value="THIS_MONTH">This month</option>
              <option value="LAST_MONTH">Last month</option>
              <option value="THIS_YEAR">This year</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Franchises scored" value={data ? data.totalFranchises.toLocaleString('en-US') : '—'} hint="by outlet operator" icon={Award} />
        <KPICard title="Avg KPI score" value={data ? `${data.avgKpiScore}` : '—'} hint="0–100 scale" tone={data && data.avgKpiScore >= 80 ? 'success' : 'warning'} />
        <KPICard title="Incentive-eligible" value={incentiveEligible !== null ? incentiveEligible.toLocaleString('en-US') : '—'} hint="score ≥ 80" tone="success" />
        <KPICard title="At-risk" value={atRisk !== null ? atRisk.toLocaleString('en-US') : '—'} hint="score < 70" tone={atRisk !== null && atRisk > 0 ? 'danger' : undefined} />
      </div>

      {query.isPending && <p className="text-sm text-muted">Loading report…</p>}
      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/readFranchiseScorecard</code> required.
        </DeferredNotice>
      )}
      {query.isError && !denied && (
        <DeferredNotice title="Report unavailable">{query.error.message}</DeferredNotice>
      )}

      {data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Franchise scorecard</h3>
            {data.stub && (
              <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                Preview data — full data coming soon
              </span>
            )}
          </div>
          {data.rows.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">No franchise data found for the period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Franchise ID</th>
                    <th className="text-left px-4 py-2">Tier</th>
                    <th className="text-right px-4 py-2">Mail items</th>
                    <th className="text-right px-4 py-2">On-time %</th>
                    <th className="text-right px-4 py-2">KPI score</th>
                    <th className="text-right px-4 py-2">Incentive</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.rows.map((row) => (
                    <tr key={row.franchiseId}>
                      <td className="px-4 py-2 font-mono text-xs">{row.franchiseId}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${TIER_COLOUR[row.tier] ?? ''}`}>
                          {row.tier}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-xs">{row.mailItemCount.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs">{row.onTimeDeliveryPct}%</td>
                      <td className="px-4 py-2 text-right text-xs font-semibold">{row.kpiScore}</td>
                      <td className="px-4 py-2 text-right text-xs">
                        {row.incentiveEligible
                          ? <span className="text-nipost-green font-medium">Eligible</span>
                          : <span className="text-muted">At risk</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ReportShell>
  );
}
